// src/auth/gateway-auth.ts
var AUTH_TYPES = {
  internal: {
    description: "Internal authentication for employees and services",
    allowedApplications: ["admin-portal", "qa-portal", "analytics-portal", "dashboard"]
  },
  external: {
    description: "External authentication for end users",
    allowedApplications: ["dashboard", "selfservice-portal"]
  }
};
var APPLICATIONS = {
  dashboard: {
    name: "User Dashboard",
    description: "Main application dashboard for end users",
    type: "external"
  },
  "admin-portal": {
    name: "Admin Portal",
    description: "Internal portal for application management",
    type: "internal"
  },
  "qa-portal": {
    name: "QA Portal",
    description: "Internal portal for quality assurance",
    type: "internal"
  },
  "selfservice-portal": {
    name: "Self-Service Portal",
    description: "Customer-facing self-service administration",
    type: "external"
  },
  "analytics-portal": {
    name: "Analytics Portal",
    description: "Internal analytics and reporting",
    type: "internal"
  }
};
function isValidAuthType(authType) {
  return authType.toLowerCase() in AUTH_TYPES;
}
function isValidApplication(application) {
  return application in APPLICATIONS;
}
function isApplicationAllowedForType(authType, application) {
  if (!isValidAuthType(authType) || !isValidApplication(application)) {
    return false;
  }
  return AUTH_TYPES[authType.toLowerCase()].allowedApplications.includes(
    application
  );
}
function canAccessApplication(authType, application) {
  if (!authType) return false;
  return isApplicationAllowedForType(authType, application);
}

// src/auth/jwt-claims.ts
var DEFAULT_CLAIMS_NAMESPACE = "https://authe.io/";
function findNamespacedClaim(payload, key) {
  const suffix = "/" + key;
  for (const k of Object.keys(payload)) {
    if (k.includes("://") && k.endsWith(suffix)) return payload[k];
  }
  return void 0;
}
function createClaimReader(namespace = DEFAULT_CLAIMS_NAMESPACE) {
  const ns = namespace.endsWith("/") ? namespace : namespace + "/";
  function readClaim(payload, key) {
    if (!payload) return void 0;
    const direct = payload[ns + key];
    if (direct !== void 0) return direct;
    const bare = payload[key];
    if (bare !== void 0) return bare;
    return findNamespacedClaim(payload, key);
  }
  function readAll(payload) {
    if (!payload) return {};
    const out = {};
    const fields = [
      "customer_id",
      "org_id",
      "tenant_id",
      "user_id",
      "customer_name",
      "org_name",
      "tenant_name",
      "user_name",
      "type",
      "application",
      "roles",
      "scopes",
      "role_permissions",
      "policy_version",
      "plan_id",
      "qa_config",
      "feature_flags",
      "region",
      "region_config",
      "continent",
      "flow_type",
      "is_demo",
      "invited_by",
      "api_key_id",
      "auth_provider",
      "is_service",
      "act",
      "impersonated",
      "impersonation_id",
      "impersonation_reason",
      "impersonation_access_level",
      "impersonation_expires_at"
    ];
    for (const field of fields) {
      const val = readClaim(payload, field);
      if (val !== void 0) {
        out[field] = val;
      }
    }
    return out;
  }
  return { readClaim, readAll, namespace: ns };
}
createClaimReader();
function decodeBase64Url(segment) {
  const b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64.padEnd(b64.length + (4 - b64.length % 4) % 4, "=");
  if (typeof atob === "function") {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  return Buffer.from(padded, "base64").toString("utf8");
}
function decodeJwtPayload(token) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const parsed = JSON.parse(decodeBase64Url(parts[1]));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// src/region/index.ts
var AWS_REGIONS = {
  // US — Active
  "us-west-2": {
    name: "US West (Oregon)",
    continent: "us",
    backupRegion: "us-east-1",
    alias: "pdx",
    fullName: "Oregon",
    enabled: true
  },
  "us-east-1": {
    name: "US East (N. Virginia)",
    continent: "us",
    backupRegion: "us-west-2",
    alias: "iad",
    fullName: "Northern Virginia",
    enabled: true
  },
  // EU — Coming Soon
  "eu-central-1": {
    name: "EU (Frankfurt)",
    continent: "eu",
    backupRegion: "eu-west-1",
    alias: "fra",
    fullName: "Frankfurt",
    enabled: false
  },
  "eu-west-1": {
    name: "EU (Ireland)",
    continent: "eu",
    backupRegion: "eu-central-1",
    alias: "dub",
    fullName: "Dublin",
    enabled: false
  },
  // AP — Coming Soon
  "ap-northeast-1": {
    name: "Asia Pacific (Tokyo)",
    continent: "ap",
    backupRegion: "ap-southeast-1",
    alias: "tyo",
    fullName: "Tokyo",
    enabled: false
  },
  "ap-southeast-1": {
    name: "Asia Pacific (Singapore)",
    continent: "ap",
    backupRegion: "ap-northeast-1",
    alias: "sin",
    fullName: "Singapore",
    enabled: false
  },
  // IN — Coming Soon
  "ap-south-1": {
    name: "Asia Pacific (Mumbai)",
    continent: "in",
    backupRegion: "ap-south-2",
    alias: "bom",
    fullName: "Mumbai",
    enabled: false
  },
  "ap-south-2": {
    name: "Asia Pacific (Hyderabad)",
    continent: "in",
    backupRegion: "ap-south-1",
    alias: "hyd",
    fullName: "Hyderabad",
    enabled: false
  }
};
function regionOrThrow(region) {
  const info = AWS_REGIONS[region];
  if (!info) throw new Error(`Invalid region: ${region}`);
  return info;
}
function convertRegionToBackupRegion(region) {
  return regionOrThrow(region).backupRegion;
}
function isRegionEnabled(region) {
  return regionOrThrow(region).enabled;
}

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/api/cache.ts
var MemoryRequestCache = class {
  static {
    brandClass(this, "api/MemoryRequestCache");
  }
  store = /* @__PURE__ */ new Map();
  maxEntries;
  defaultTtlMs;
  constructor(opts = {}) {
    this.maxEntries = opts.maxEntries ?? 200;
    this.defaultTtlMs = opts.defaultTtlMs ?? 3e4;
  }
  get(key) {
    const entry = this.store.get(key);
    if (!entry) return void 0;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return void 0;
    }
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value;
  }
  set(key, value, ttlMs = this.defaultTtlMs) {
    if (this.store.size >= this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest !== void 0) this.store.delete(oldest);
    }
    this.store.set(key, { value, expiresAt: Date.now() + ttlMs });
  }
  delete(key) {
    this.store.delete(key);
  }
  clear() {
    this.store.clear();
  }
  keys() {
    return [...this.store.keys()];
  }
};
var LocalStorageRequestCache = class {
  static {
    brandClass(this, "api/LocalStorageRequestCache");
  }
  prefix;
  getStorage;
  constructor(opts = {}) {
    this.prefix = `${opts.namespace ?? "authe:api-cache"}:`;
    this.getStorage = opts.getStorage ?? (() => typeof localStorage !== "undefined" ? localStorage : void 0);
  }
  keyFor(key) {
    return `${this.prefix}${key}`;
  }
  get(key) {
    const storage2 = this.getStorage();
    if (!storage2) return void 0;
    try {
      const raw = storage2.getItem(this.keyFor(key));
      if (!raw) return void 0;
      const entry = JSON.parse(raw);
      if (Date.now() > entry.expiresAt) {
        storage2.removeItem(this.keyFor(key));
        return void 0;
      }
      return entry.value;
    } catch {
      return void 0;
    }
  }
  set(key, value, ttlMs) {
    const storage2 = this.getStorage();
    if (!storage2) return;
    try {
      const entry = { value, expiresAt: Date.now() + ttlMs };
      storage2.setItem(this.keyFor(key), JSON.stringify(entry));
    } catch {
    }
  }
  delete(key) {
    const storage2 = this.getStorage();
    if (!storage2) return;
    try {
      storage2.removeItem(this.keyFor(key));
    } catch {
    }
  }
  clear() {
    const storage2 = this.getStorage();
    if (!storage2) return;
    try {
      const toRemove = [];
      for (let i = 0; i < storage2.length; i += 1) {
        const k = storage2.key(i);
        if (k && k.startsWith(this.prefix)) toRemove.push(k);
      }
      for (const k of toRemove) storage2.removeItem(k);
    } catch {
    }
  }
  keys() {
    const storage2 = this.getStorage();
    if (!storage2) return [];
    try {
      const out = [];
      for (let i = 0; i < storage2.length; i += 1) {
        const k = storage2.key(i);
        if (k && k.startsWith(this.prefix)) out.push(k.slice(this.prefix.length));
      }
      return out;
    } catch {
      return [];
    }
  }
};
var NO_CACHE = {
  get: () => void 0,
  set: () => void 0,
  delete: () => void 0,
  clear: () => void 0,
  keys: () => []
};
function cacheKey(method, url, body) {
  const b = body == null ? "" : typeof body === "string" ? body : JSON.stringify(body);
  return `${method.toUpperCase()} ${url} ${b}`;
}

// src/display/coerce.ts
function asString(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}

// src/storage/index.ts
(class {
  constructor(fetch2) {
    this.fetch = fetch2;
  }
  fetch;
  static {
    brandClass(this, "storage/LazyBody");
  }
  cache = null;
  _loaded = false;
  inflight = null;
  async get() {
    if (this._loaded && this.cache !== null) return this.cache;
    if (!this.inflight) {
      this.inflight = this.fetch().then((v) => {
        this.cache = v;
        this._loaded = true;
        this.inflight = null;
        return v;
      });
    }
    return this.inflight;
  }
  get loaded() {
    return this._loaded;
  }
});
function safeLocalStorage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
var STORAGE_NAMESPACE = "authe";
function storageKey(...parts) {
  return [STORAGE_NAMESPACE, ...parts.map(String).filter(Boolean)].join(":");
}
function readLocalJson(key, fallback) {
  const value = readLocalJsonOptional(key);
  return value === void 0 ? fallback : value;
}
function readLocalJsonOptional(key) {
  const store = safeLocalStorage();
  if (!store) return void 0;
  try {
    const raw = store.getItem(key);
    if (raw === null) return void 0;
    return JSON.parse(raw);
  } catch {
    return void 0;
  }
}
function writeLocalJson(key, value) {
  const store = safeLocalStorage();
  if (!store) return false;
  try {
    store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
function removeLocalKey(key) {
  safeLocalStorage()?.removeItem(key);
}
function localKeysWithPrefix(prefix) {
  const store = safeLocalStorage();
  if (!store) return [];
  const out = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key != null && key.startsWith(prefix)) out.push(key);
  }
  return out;
}
function clearLocalPrefix(prefix) {
  const store = safeLocalStorage();
  if (!store) return 0;
  const keys = localKeysWithPrefix(prefix);
  for (const key of keys) store.removeItem(key);
  return keys.length;
}
var STORAGE_LEVELS = [
  "user",
  "tenant",
  "org",
  "customer",
  "app",
  "global"
];
function isGlobalish(value) {
  if (value == null) return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === "main";
}
function identitySegments(identity, level) {
  const segs = [STORAGE_NAMESPACE];
  if (level === "global") return segs;
  if (identity.app) segs.push(String(identity.app));
  if (level === "app") return segs;
  if (!isGlobalish(identity.customerId)) segs.push("c", String(identity.customerId));
  if (level === "customer") return segs;
  if (!isGlobalish(identity.orgId)) segs.push("o", String(identity.orgId));
  if (level === "org") return segs;
  if (!isGlobalish(identity.tenantId)) segs.push("t", String(identity.tenantId));
  if (level === "tenant") return segs;
  if (identity.userId) segs.push("u", String(identity.userId));
  return segs;
}
(class _ScopedStorage {
  static {
    brandClass(this, "storage/ScopedStorage");
  }
  identity;
  /** The level `set`/`remove` use when none is given. */
  defaultLevel;
  constructor(identity) {
    this.identity = { ...identity };
    this.defaultLevel = identity.userId ? "user" : !isGlobalish(identity.tenantId) ? "tenant" : !isGlobalish(identity.orgId) ? "org" : "app";
  }
  /** The fully-namespaced key for `name` at `level`. */
  keyAt(name, level) {
    return [...identitySegments(this.identity, level), name].join(":");
  }
  /** Read exactly at one level (no fallback up the hierarchy). */
  getAt(name, level, fallback) {
    const value = readLocalJsonOptional(this.keyAt(name, level));
    return value === void 0 ? fallback : value;
  }
  /** Hierarchical read: most specific stored value wins, else `fallback`. */
  get(name, fallback) {
    const tried = /* @__PURE__ */ new Set();
    for (const level of STORAGE_LEVELS) {
      const key = this.keyAt(name, level);
      if (tried.has(key)) continue;
      tried.add(key);
      const value = readLocalJsonOptional(key);
      if (value !== void 0) return value;
    }
    return fallback;
  }
  /** Write at `level` (default: `defaultLevel`). */
  set(name, value, level = this.defaultLevel) {
    return writeLocalJson(this.keyAt(name, level), value);
  }
  /** Remove at `level` (default: `defaultLevel`). */
  remove(name, level = this.defaultLevel) {
    removeLocalKey(this.keyAt(name, level));
  }
  /** Remove every key at `level` and narrower for this identity. Returns the count. */
  clear(level) {
    return clearLocalPrefix(`${identitySegments(this.identity, level).join(":")}:`);
  }
  /** Derive a new scope with some identity fields changed (e.g. switch tenant). */
  with(patch) {
    return new _ScopedStorage({ ...this.identity, ...patch });
  }
});

// src/config/endpoints.ts
var ApiEndpointMode = {
  CLOUDFRONT: "cloudfront",
  GATEWAY: "gateway"
};

// src/config/debug.ts
var DEBUG_QUERY_KEYS = {
  endpoint: "api",
  region: "region",
  debug: "debug"
};
var DEBUG_STICKY_KEY = storageKey("config", "debug");
function coerceMode(value) {
  if (value === ApiEndpointMode.GATEWAY || value === "gw" || value === "apigw") return ApiEndpointMode.GATEWAY;
  if (value === ApiEndpointMode.CLOUDFRONT || value === "cf" || value === "cdn") return ApiEndpointMode.CLOUDFRONT;
  return void 0;
}
function parseDebugParams(search) {
  const qs = (typeof window !== "undefined" ? window.location.search : "");
  const params = new URLSearchParams(qs);
  const raw = {};
  for (const [k, v] of params.entries()) raw[k] = v;
  const out = { raw };
  const mode = coerceMode(params.get(DEBUG_QUERY_KEYS.endpoint));
  if (mode) out.endpointMode = mode;
  const region = params.get(DEBUG_QUERY_KEYS.region);
  if (region) out.region = region;
  const debug = params.get(DEBUG_QUERY_KEYS.debug);
  if (debug != null) out.verbose = debug !== "0" && debug !== "false";
  return out;
}
function getDebugParams(search) {
  const fromUrl = parseDebugParams();
  const sticky = readLocalJson(DEBUG_STICKY_KEY, null);
  if (fromUrl.verbose) {
    writeLocalJson(DEBUG_STICKY_KEY, fromUrl);
    return fromUrl;
  }
  if (fromUrl.verbose === false) {
    removeLocalKey(DEBUG_STICKY_KEY);
    return fromUrl;
  }
  if (!sticky) return fromUrl;
  return { ...sticky, ...fromUrl, raw: { ...sticky.raw, ...fromUrl.raw } };
}
function isDebugEnabled2(search) {
  return getDebugParams().verbose === true;
}

// src/blue-green/index.ts
var BLUE = "blue";
var GREEN = "green";
function normaliseColour(value, fallback) {
  const v = String(value ?? "").toLowerCase();
  if (v === BLUE) return BLUE;
  if (v === GREEN) return GREEN;
  if (v === "true" || v === "1") return BLUE;
  if (v === "false" || v === "0") return GREEN;
  return fallback;
}
function normaliseServices(value) {
  const out = {};
  if (value && typeof value === "object" && !Array.isArray(value)) {
    for (const [service, colour] of Object.entries(value)) {
      if (service) out[service] = normaliseColour(colour, GREEN);
    }
  }
  return out;
}
var BlueRoutingConfig = class _BlueRoutingConfig {
  static {
    brandClass(this, "blue-green/BlueRoutingConfig");
  }
  default;
  services;
  constructor(deflt = GREEN, services = {}) {
    this.default = deflt;
    this.services = { ...services };
  }
  /**
   * Resolve the effective colour for `service` (override else default). An
   * override may name the service or the path its API is mounted on
   * ({@link serviceKeysFor}), matching the Python `colour_for`.
   */
  colourFor(service) {
    for (const key of serviceKeysFor(service)) {
      const colour = this.services[key];
      if (colour) return colour;
    }
    return this.default;
  }
  /** True when `service` resolves to the blue (latest) deploy. */
  isBlueFor(service) {
    return this.colourFor(service) === BLUE;
  }
  /** True when every service resolves to blue (no green overrides). */
  isGlobalBlue() {
    return this.default === BLUE && !Object.values(this.services).includes(GREEN);
  }
  /** True when nothing routes to blue (the normal, non-QA state). */
  isAllGreen() {
    return this.default === GREEN && !Object.values(this.services).includes(BLUE);
  }
  /** Service names explicitly pinned to a given colour. */
  servicesWithColour(colour) {
    return Object.keys(this.services).filter((s) => this.services[s] === colour);
  }
  /** Plain-object form for persistence / the JWT claim / equality checks. */
  toJSON() {
    return { default: this.default, services: { ...this.services } };
  }
  /** A fresh "everything green" config (the normal, non-QA state). */
  static allGreen() {
    return new _BlueRoutingConfig(GREEN, {});
  }
  /** A fresh "everything blue" config (global blue). */
  static globalBlue() {
    return new _BlueRoutingConfig(BLUE, {});
  }
  /**
   * Coerce a stored/claimed value into the structured shape.
   *
   * Tolerant of the LEGACY flat service-whitelist (`string[]`, where an empty
   * list meant "everything blue") so a pre-prod cutover and any in-flight tokens
   * keep working. Matches the Python `BlueRoutingConfig.from_value`:
   *
   *   - `null` / `{}` / `[]`               → `default=blue` (legacy empty = all blue)
   *   - `["authentication", "permissions"]`→ `default=green` + those services blue
   *   - `{ default, services }`            → structured config
   *   - bare `{ service: colour }` map     → `default=green` + that map
   */
  static fromValue(value) {
    if (value === null || value === void 0) return _BlueRoutingConfig.globalBlue();
    if (value instanceof _BlueRoutingConfig) return value;
    if (Array.isArray(value)) {
      const services = {};
      for (const item of value) if (item) services[String(item)] = BLUE;
      if (Object.keys(services).length === 0) return _BlueRoutingConfig.globalBlue();
      return new _BlueRoutingConfig(GREEN, services);
    }
    if (typeof value === "object") {
      const obj = value;
      if (Object.keys(obj).length === 0) return _BlueRoutingConfig.globalBlue();
      if ("default" in obj || "services" in obj) {
        return new _BlueRoutingConfig(
          normaliseColour(obj.default, GREEN),
          normaliseServices(obj.services)
        );
      }
      return new _BlueRoutingConfig(GREEN, normaliseServices(obj));
    }
    return _BlueRoutingConfig.allGreen();
  }
};
function routingFromQaConfig(qa) {
  if (!qa) return BlueRoutingConfig.allGreen();
  const cfg = qa.config;
  if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
    return BlueRoutingConfig.fromValue(cfg);
  }
  if (qa.environment !== BLUE) return BlueRoutingConfig.allGreen();
  return BlueRoutingConfig.fromValue(cfg ?? []);
}
var SERVICE_PATH_ALIASES = {
  "internal/addresses": "address-mgmt-api-internal",
  "internal/tools/blue-green-mgmt": "blue-green-mgmt-api",
  "internal/tools/lambda-warmer-mgmt": "lambda-warmer-mgmt",
  "internal/tools/release-management": "release-mgmt-api",
  management: "management-api",
  "management/permissions": "permissions",
  "private/abis": "abi-mgmt-api",
  "private/addresses": "address-mgmt-api",
  "private/notifications": "notification-center-api",
  "private/search": "search-mgmt-api",
  "private/streams": "streaming-mgmt-api",
  "private/token-flows": "token-flow-mgmt-api",
  "private/transactions": "transactions-mgmt-api",
  "private/transactions-graph": "transactions-graph-api"
};
function serviceKeysFor(service) {
  const keys = [service];
  for (const [path, name] of Object.entries(SERVICE_PATH_ALIASES)) {
    if (name === service) keys.push(path, path.slice(path.lastIndexOf("/") + 1));
  }
  return keys;
}
var ROUTING_SEGMENT = /^(?:blue|GET|ANY|POST|PUT|PATCH|DELETE|HEAD|OPTIONS|v\d+)$/;
function servicePathOf(url) {
  let pathname;
  try {
    pathname = new URL(url, "http://relative.invalid").pathname;
  } catch {
    pathname = url.split(/[?#]/)[0] ?? "";
  }
  const segments = pathname.split("/").filter(Boolean);
  const end = segments.findIndex((segment) => ROUTING_SEGMENT.test(segment));
  return end === -1 ? segments : segments.slice(0, end);
}
function serviceKeysForPath(path) {
  const keys = /* @__PURE__ */ new Set();
  for (let start = 0; start < path.length; start++) {
    for (let end = start + 1; end <= path.length; end++) {
      keys.add(path.slice(start, end).join("/"));
    }
  }
  const alias = SERVICE_PATH_ALIASES[path.join("/")];
  if (alias) keys.add(alias);
  return keys;
}
function normaliseServiceKey(key) {
  return key.trim().replace(/^\/+|\/+$/g, "");
}
function colourForUrl(routing, url) {
  const candidates = serviceKeysForPath(servicePathOf(url));
  let bestColour = null;
  let bestLen = -1;
  for (const [service, colour] of Object.entries(routing.services)) {
    const key = normaliseServiceKey(service);
    if (key.length > bestLen && candidates.has(key)) {
      bestColour = colour;
      bestLen = key.length;
    }
  }
  return bestColour ?? routing.default;
}

// src/api/region-routing.ts
var REGION_ALIASES = [
  { alias: "pdx", region: "us-west-2", label: "US West (Oregon)" },
  { alias: "iad", region: "us-east-1", label: "US East (N. Virginia)" },
  { alias: "fra", region: "eu-central-1", label: "EU Central (Frankfurt)" },
  { alias: "dub", region: "eu-west-1", label: "EU West (Ireland)" }
];
var DEFAULT_REGION_ALIAS = "default";
function regionAlias(alias) {
  return REGION_ALIASES.find((r) => r.alias === alias);
}
var log = createLogger("fe-commons:api:routing");
function storageFor(config) {
  if (config.getStorage) return config.getStorage();
  return typeof localStorage !== "undefined" ? localStorage : void 0;
}
function claim(token, config, suffix) {
  const ns = config.claimsNamespace.endsWith("/") ? config.claimsNamespace : `${config.claimsNamespace}/`;
  const direct = token[ns + suffix];
  if (direct !== void 0) return direct;
  const bare = token[suffix];
  if (bare !== void 0) return bare;
  const tail = `/${suffix}`;
  for (const key of Object.keys(token)) {
    if (key.includes("://") && key.endsWith(tail)) return token[key];
  }
  return void 0;
}
function localStoragePrefix(token, config) {
  const orgId = claim(token, config, "org_id");
  const tenantId = claim(token, config, "tenant_id");
  return `${config.localStoragePrefixPlatform}-${config.env}-${token.sub}-${orgId}-${tenantId}`;
}
function resolveRegion(token, config) {
  const regionConfig = claim(token, config, "region_config") ?? {};
  const region = regionConfig.region ?? "";
  const regionAlias2 = regionConfig.region_alias ?? "";
  const backupRegion = regionConfig.backup_region ?? "";
  const backupRegionAlias = regionConfig.backup_region_alias ?? "";
  const storage2 = storageFor(config);
  const prefix = localStoragePrefix(token, config);
  let storageUseRegionAlias = "default";
  try {
    storageUseRegionAlias = JSON.parse(storage2?.getItem(`${prefix}_use_region_alias`) ?? '"default"');
  } catch {
    storageUseRegionAlias = "default";
  }
  if (storageUseRegionAlias && storageUseRegionAlias !== "default") {
    if (storageUseRegionAlias === regionAlias2) {
      return {
        useBackupRegion: backupRegion,
        useBackupRegionAlias: backupRegionAlias,
        useRegion: region,
        useRegionAlias: regionAlias2
      };
    }
    return {
      useBackupRegion: region,
      useBackupRegionAlias: storageUseRegionAlias,
      useRegion: backupRegion,
      useRegionAlias: backupRegionAlias
    };
  }
  const now = (/* @__PURE__ */ new Date()).getUTCMinutes() % 5;
  if (now > 2) {
    (config.debug ?? log.debug)(`Using back up region: ${backupRegionAlias}`);
    return {
      useBackupRegion: regionAlias2,
      useBackupRegionAlias: regionAlias2,
      useRegion: backupRegion,
      useRegionAlias: backupRegionAlias
    };
  }
  (config.debug ?? log.debug)(`Using main region: ${regionAlias2}`);
  return {
    useBackupRegion: backupRegion,
    useBackupRegionAlias: backupRegionAlias,
    useRegion: region,
    useRegionAlias: regionAlias2
  };
}
var PRIVATE_BARE_MOUNTS = /* @__PURE__ */ new Set([
  "abis",
  "related-entities",
  "streams",
  "streams-admin",
  "token-flows",
  "transactions"
]);
function privateService(url) {
  const match = /\/private\/([^/]+)\//.exec(url);
  return match?.[1] ?? "";
}
function rewriteUrl(url, token, config, resolved) {
  const continent = (claim(token, config, "continent") ?? "").toLowerCase();
  const domain = config.apiBaseDomain;
  const continentUpper = claim(token, config, "continent") ?? "";
  const alias = resolved.useRegionAlias;
  let out = url;
  const oldUrl = url;
  if (url.includes("/public/")) {
    const endpoint2 = `https://${alias}.${continent}.public.${domain}`;
    out = out.replace(`https://api.${domain}`, endpoint2);
    (config.debug ?? log.debug)(`Changed the API Endpoint to: ${out}`);
    return applyBlueGreen(out, token, config);
  }
  if (url.includes("/private/")) {
    const endpoint2 = `https://${alias}.${continent}.private.${domain}`;
    if (oldUrl.includes("/GET/")) {
      out = out.replace("GET/v0/", `GET/${continentUpper}/v0/`);
      if (out.includes("/transactions-search/") || out.includes("/entities/") || out.includes("/addresses/") || out.includes("/abis/") || out.includes("/transactions/") || out.includes("/token-flows/") || // `streams-admin` (streamed-transactions-api-internal) is mapped at the
      // bare `streams-admin` base path on the `.private.` host (the `.api.`
      // zone exposes it as `private/streams-admin`), so reads strip `/private`
      // just like the directly-mounted private services above. Writes already
      // strip it in the ANY branch below.
      out.includes("/streams-admin/")) {
        out = out.replace(`https://api.${domain}/private`, endpoint2);
      } else {
        out = out.replace(`https://api.${domain}`, endpoint2);
      }
    } else {
      out = out.replace("ANY/v0/", `ANY/${continentUpper}/v0/`);
      if (PRIVATE_BARE_MOUNTS.has(privateService(out))) {
        out = out.replace(`https://api.${domain}/private`, endpoint2);
      } else {
        out = out.replace(`https://api.${domain}`, endpoint2);
      }
    }
    (config.debug ?? log.debug)(`Changed the API Endpoint to: ${out}`);
    return applyBlueGreen(out, token, config);
  }
  if (url.includes("/management/")) {
    const endpoint2 = `https://${alias}.${continent}.management.${domain}`;
    if (oldUrl.includes("/GET/")) {
      out = out.replace("GET/v0/", `GET/${continentUpper}/v0/`);
      out = out.replace(`https://api.${domain}`, endpoint2);
    } else {
      out = out.replace("ANY/v0/", `ANY/${continentUpper}/v0/`);
      out = out.replace(`https://api.${domain}`, endpoint2);
    }
    if (out.includes("/management/actions/")) out = out.replace("/management/actions/", "/actions/");
    if (out.includes("/management/integrations/"))
      out = out.replace("/management/integrations/", "/integrations/");
    (config.debug ?? log.debug)(`Changed the API Endpoint to: ${out}`);
    return applyBlueGreen(out, token, config);
  }
  if (url.includes("/internal/")) {
    const endpoint2 = `https://${alias}.${continent}.internal.${domain}`;
    if (oldUrl.includes("/GET/")) {
      out = out.replace("GET/v0/", `GET/${continentUpper}/v0/`);
    } else if (oldUrl.includes("/ANY/")) {
      out = out.replace("ANY/v0/", `ANY/${continentUpper}/v0/`);
    } else if (oldUrl.includes("/POST/")) {
      out = out.replace("POST/v0/", `POST/${continentUpper}/v0/`);
    }
    out = out.replace(`https://api.${domain}`, endpoint2);
    (config.debug ?? log.debug)(`Changed the API Endpoint to: ${out}`);
    return applyBlueGreen(out, token, config);
  }
  const endpoint = `https://${alias}.${continent}.private.${domain}`;
  out = out.replace(`https://api.${domain}`, endpoint);
  (config.debug ?? log.debug)(`Changed the API Endpoint to: ${out}`);
  return applyBlueGreen(out, token, config);
}
function rewriteRegionApiUrl(path, token, config, resolved, method) {
  const continentRaw = claim(token, config, "continent") ?? "";
  const continent = continentRaw.toLowerCase();
  const alias = resolved.useRegionAlias;
  if (!alias || !continent) return null;
  const verb = method.toUpperCase() === "GET" ? "GET" : "ANY";
  const withVerb = path.replace("/v0/", `/${verb}/${continentRaw.toUpperCase()}/v0/`);
  const zone = withVerb.includes("/management/") ? "management" : "api";
  const host = `https://${alias}.${continent}.${zone}.${config.apiBaseDomain}`;
  const full = `${host}${withVerb.startsWith("/") ? "" : "/"}${withVerb}`;
  const out = applyBlueGreen(full, token, config);
  (config.debug ?? log.debug)(`Region API endpoint (${verb}): ${out}`);
  return out;
}
function routingForToken(token, config) {
  return routingFromQaConfig(claim(token, config, "qa_config"));
}
function deploymentColourForService(token, config, service) {
  const qaConfig = claim(token, config, "qa_config");
  return routingFromQaConfig(qaConfig).colourFor(service);
}
function rewriteRegionGraphqlUrl(token, config, resolved, colour = GREEN) {
  const continent = (claim(token, config, "continent") ?? "").toLowerCase();
  const alias = resolved.useRegionAlias;
  if (!alias || !continent) return null;
  const label = colour === BLUE ? "graphql-blue" : "graphql";
  const out = `https://${label}.${alias}.${continent}.private.${config.apiBaseDomain}/graphql`;
  (config.debug ?? log.debug)(`Region GraphQL endpoint (${colour}): ${out}`);
  return out;
}
function jwtPayload(jwt) {
  const part = jwt.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = typeof atob === "function" ? decodeURIComponent(
      Array.from(atob(base64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join("")
    ) : Buffer.from(base64, "base64").toString("utf8");
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
function routingTokenForBearer(token, bearer, claimsNamespace) {
  const payload = bearer ? jwtPayload(bearer) : null;
  if (!payload) return token;
  const ns = claimsNamespace.endsWith("/") ? claimsNamespace : `${claimsNamespace}/`;
  const qa = Object.entries(payload).find(([key]) => key === `${ns}qa_config` || key.endsWith("/qa_config"))?.[1];
  const out = { ...token };
  for (const key of Object.keys(out)) {
    if (key === "qa_config" || key.endsWith("/qa_config")) delete out[key];
  }
  if (qa !== void 0) out[`${ns}qa_config`] = qa;
  return out;
}
var STREAMING_SERVICE = "streaming-mgmt-api";
function appsyncColourUrl(url, colour) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.hostname.endsWith(".amazonaws.com")) return url;
  const [label, ...rest] = parsed.hostname.split(".");
  if (!label || rest.length === 0) return url;
  const base = label.replace(/-blue$/, "");
  const next = colour === BLUE ? `${base}-blue` : base;
  if (next === label) return url;
  parsed.hostname = [next, ...rest].join(".");
  return parsed.toString();
}
function applyBlueGreen(url, token, config) {
  const qaConfig = claim(token, config, "qa_config");
  const routing = routingFromQaConfig(qaConfig);
  if (colourForUrl(routing, url) !== "blue") return url;
  const out = url.replace("/GET/", "/blue/GET/").replace("/ANY/", "/blue/ANY/");
  (config.debug ?? log.debug)(`New blue config url = ${out}`);
  return out;
}
function buildAutheHeaders(token, config, resolved, extra) {
  return {
    Authorization: `Bearer ${token.__raw}`,
    Accept: "application/json, text/plain, */*",
    "Source-Platform": config.sourcePlatform,
    "Source-Region": resolved.useRegion,
    "Destination-Region": resolved.useRegion,
    ...extra ?? {}
  };
}
function unwrapEnvelope(data) {
  let body = data;
  if (body && typeof body === "object" && "result" in body) {
    body = body.result;
  }
  const maybe = body;
  if (maybe && typeof maybe === "object" && maybe.ok === false) {
    const error = maybe.error;
    const showMessage = error?.message === "unexpected error" ? error?.reason : error?.message;
    const httpError = {
      message: showMessage ?? "Request failed",
      statusCode: maybe.statusCode
    };
    throw httpError;
  }
  return body;
}

// src/async/backoff.ts
var DEFAULT_BACKOFF = {
  baseMs: 1e3,
  maxMs: 3e4,
  factor: 2,
  jitter: 0.3
};
function computeBackoff(attempt, options = {}) {
  const { baseMs, maxMs, factor, jitter } = { ...DEFAULT_BACKOFF, ...options };
  const capped = Math.min(maxMs, baseMs * factor ** Math.max(0, attempt));
  const fraction = Math.min(1, Math.max(0, jitter));
  return Math.round(capped - capped * fraction * Math.random());
}

// src/async/retry.ts
function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new Error("aborted"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason ?? new Error("aborted"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
async function retryAsync(fn, options = {}) {
  const { retries = 3, shouldRetry = () => true, onRetry, signal, sleep = delay } = options;
  let attempt = 0;
  for (; ; ) {
    try {
      return await fn(attempt);
    } catch (error) {
      if (attempt >= retries || signal?.aborted || !shouldRetry(error, attempt)) throw error;
      const ms = computeBackoff(attempt, options);
      onRetry?.(error, attempt, ms);
      await sleep(ms, signal);
      attempt += 1;
    }
  }
}

// src/async/singleflight.ts
function singleflight() {
  const inflight = /* @__PURE__ */ new Map();
  return {
    run(key, fn) {
      const existing = inflight.get(key);
      if (existing) return existing;
      const promise = Promise.resolve().then(fn);
      inflight.set(key, promise);
      const clear = () => {
        if (inflight.get(key) === promise) inflight.delete(key);
      };
      promise.then(clear, clear);
      return promise;
    },
    forget(key) {
      inflight.delete(key);
    },
    get size() {
      return inflight.size;
    }
  };
}

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  // NOT a tenancy refusal, despite what this line used to say ("You do not have
  // access to this resource within your tenant"). PERM002 is the authorizer's
  // general deny: a wrong audience, an app/env or application-type mismatch, an
  // expired or incomplete impersonation session, a revoked API key, an AWS
  // account that is not allowed, a blue-route mismatch, an unregistered public
  // API, or a gateway not whitelisted for the calling application. None of those
  // is tenancy — though a handler-level cross-tenant refusal carries PERM002
  // too, so tenancy is one cause among many and the line can no longer name
  // just that one. Mirrors `commons.errors.codes`.
  PERM002: spec("PERM002", "Resource access denied", "Refused before the service ran. Signing in again clears an expired or mismatched session; otherwise the resource belongs to another tenant, or this application isn't registered to reach that API.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // A plan's usage quota is used up for its window. 429, but NOT retryable: an
  // unknown 429 is retried by the client, and re-sending a refused run cannot
  // clear a monthly cap. `details.resets_at` says when it does.
  QUOTA001: spec("QUOTA001", "Usage quota reached", "Wait until the quota resets, or move to a plan with a higher quota. The message says which quota and when it resets.", ErrorCategory.RATE_LIMIT, 429, { severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}
var AutheError = class _AutheError extends Error {
  static {
    brandClass(this, "errors/AutheError");
  }
  spec;
  details;
  correlationId;
  constructor(spec2, opts = {}) {
    super(opts.message ?? spec2.description);
    this.name = "AutheError";
    this.spec = spec2;
    this.details = opts.details ?? {};
    this.correlationId = opts.correlationId;
    if (opts.cause !== void 0) this.cause = opts.cause;
  }
  /** Look up a code by string id and build an `AutheError` (falls back to INT001). */
  static fromCode(code, opts = {}) {
    return new _AutheError(getCodeSpec(code) ?? ErrorCode.INT001, opts);
  }
  get retryable() {
    return this.spec.retryable;
  }
  toBody() {
    return {
      message: this.message,
      error_type: this.name,
      error_code: this.spec.code,
      error_level: this.spec.severity,
      details: this.details,
      correlation_id: this.correlationId ?? null
    };
  }
  toResponse() {
    return { statusCode: this.spec.defaultStatus, ok: false, error: this.toBody() };
  }
};
var STATUS_FALLBACK = [
  [401, ErrorCode.AUTH001],
  [403, ErrorCode.PERM001],
  [404, ErrorCode.RES001],
  [409, ErrorCode.RES002],
  [429, ErrorCode.RATE001]
];
function withCatalogCode(response) {
  const code = errorCodeOf(response.error);
  if (!code || code === response.error.error_code) return response;
  return { ...response, error: { ...response.error, error_code: code } };
}
function withStatusCode(response) {
  if (response.error.error_code) return response;
  const spec2 = STATUS_FALLBACK.find(([status]) => status === response.statusCode)?.[1];
  if (!spec2) return response;
  return {
    ...response,
    error: {
      ...response.error,
      error_code: spec2.code,
      error_level: response.error.error_level ?? spec2.severity
    }
  };
}
function toErrorResponse(err) {
  if (err instanceof AutheError) return err.toResponse();
  if (err && typeof err === "object" && "ok" in err && err.ok === false) {
    return withCatalogCode(err);
  }
  if (err && typeof err === "object" && "envelope" in err) {
    const envelope = err.envelope;
    if (envelope && typeof envelope === "object" && envelope.ok === false) {
      return withStatusCode(withCatalogCode(envelope));
    }
  }
  const info = graphqlErrorInfoOf(err);
  if (info) {
    const response = withCatalogCode({
      statusCode: 0,
      ok: false,
      error: { details: {}, ...info, correlation_id: info.correlation_id ?? correlationIdOf(err) }
    });
    const spec3 = ErrorCode[response.error.error_code];
    return { ...response, statusCode: spec3?.defaultStatus ?? 0 };
  }
  const message = err instanceof Error ? err.message : String(err);
  const status = err && typeof err === "object" ? err.statusCode : void 0;
  const mapped = typeof status === "number" ? STATUS_FALLBACK.find(([code]) => code === status)?.[1] : void 0;
  const spec2 = mapped ?? ErrorCode.INT001;
  return {
    statusCode: spec2.defaultStatus,
    ok: false,
    error: {
      message,
      error_type: err instanceof Error ? err.name : "Error",
      error_code: spec2.code,
      error_level: spec2.severity,
      details: {},
      correlation_id: correlationIdOf(err)
    }
  };
}
function graphqlErrorInfoOf(err) {
  if (!err || typeof err !== "object") return null;
  const errors = err.errors;
  if (!Array.isArray(errors)) return null;
  for (const entry of errors) {
    const info = entry && typeof entry === "object" ? entry.errorInfo : null;
    if (!info || typeof info !== "object") continue;
    const { code, error_code: errorCode, message } = info;
    if (typeof code !== "string" && typeof errorCode !== "string") continue;
    return {
      ...info,
      message: typeof message === "string" ? message : String(entry.message ?? "")
    };
  }
  return null;
}
function correlationIdOf(err) {
  if (!err || typeof err !== "object") return null;
  const value = err.correlationId;
  return typeof value === "string" && value.length > 0 ? value : null;
}

// src/api/debug.ts
var FIELD_KINDS = /* @__PURE__ */ new Set([
  "text",
  "id",
  "timestamp",
  "duration_ms",
  "bytes",
  "number",
  "ratio",
  "json"
]);
function rec(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function coerceField(raw) {
  const o = rec(raw);
  const label = asString(o?.label);
  if (!o || !label) return null;
  const kind = asString(o.kind);
  return {
    key: asString(o.key) ?? label,
    label,
    // `value` is read with `in` rather than a truthiness test: `0`, `false` and
    // `null` are all legitimate diagnostics.
    value: "value" in o ? o.value : null,
    kind: kind && FIELD_KINDS.has(kind) ? kind : "text",
    ...asString(o.hint) ? { hint: asString(o.hint) } : {}
  };
}
function coerceGroup(raw) {
  const o = rec(raw);
  const label = asString(o?.label);
  if (!o || !label) return null;
  const fields = (Array.isArray(o.fields) ? o.fields : []).map(coerceField).filter((f) => f !== null);
  if (fields.length === 0) return null;
  return { key: asString(o.key) ?? label, label, fields };
}
function coerceResource(raw) {
  const o = rec(raw);
  const resource = asString(o?.resource);
  if (!o || !resource) return null;
  const groups = (Array.isArray(o.groups) ? o.groups : []).map(coerceGroup).filter((g) => g !== null);
  if (groups.length === 0) return null;
  return {
    resource,
    label: asString(o.label) ?? resource,
    id: asString(o.id) ?? null,
    groups
  };
}
function readResponseDebug(envelope) {
  const block = rec(rec(envelope)?.debug);
  if (!block) return void 0;
  const resources = (Array.isArray(block.resources) ? block.resources : []).map(coerceResource).filter((r) => r !== null);
  return { ...block, ...resources.length ? { resources } : {} };
}
function readDebugResources(envelope) {
  return readResponseDebug(envelope)?.resources ?? [];
}
var DebugResourceCaptureSink = class {
  byKind = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  snapshot = [];
  /**
   * Read any resources off `envelope` and record them against `path`.
   *
   * Cheap and total: a response with no debug block (the normal case, and every
   * response when debug is off) costs one property read and notifies nobody.
   */
  capture(path, envelope) {
    const resources = readDebugResources(envelope);
    if (resources.length === 0) return;
    const at = Date.now();
    for (const resource of resources) {
      this.byKind.set(resource.resource, { ...resource, path, at });
    }
    this.publish();
  }
  /** Drop everything (navigation: the previous page's record is off screen). */
  clear() {
    if (this.byKind.size === 0) return;
    this.byKind.clear();
    this.publish();
  }
  /** Everything captured, newest-captured last. Stable between changes. */
  list = () => this.snapshot;
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  publish() {
    this.snapshot = [...this.byKind.values()];
    for (const listener of this.listeners) listener();
  }
};
var debugResourceSink = globalSingleton(
  "api/debug-resource-sink",
  () => new DebugResourceCaptureSink()
);

// src/api/client.ts
var log2 = createLogger("fe-commons:api");
var ALIAS_TO_REGION = {
  pdx: "us-west-2",
  iad: "us-east-1",
  fra: "eu-central-1",
  dub: "eu-west-1"
};
function pickActiveRegionUrl(primary, backup, now = /* @__PURE__ */ new Date()) {
  if (!backup) return primary;
  return now.getUTCMinutes() % 5 > 2 ? backup : primary;
}
function buildQueryString(query) {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item == null || item === "") continue;
        params.append(key, String(item));
      }
    } else {
      params.set(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}
var RetryableHttpError = class extends Error {
  constructor(envelope, retryAfterMs) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.retryAfterMs = retryAfterMs;
    this.name = "RetryableHttpError";
  }
  envelope;
  retryAfterMs;
};
function isRetryableResponse(status, code) {
  if (code) {
    const spec2 = getCodeSpec(code);
    if (spec2) return spec2.retryable;
  }
  return status >= 500 || status === 429;
}
var EXPIRY_SKEW_MS = 3e4;
function sentBearer(headers) {
  const value = headers.Authorization ?? headers.authorization;
  if (!value) return null;
  const match = /^Bearer\s+(.+)$/i.exec(value);
  return match?.[1] ?? null;
}
function bearerExpired(bearer) {
  if (!bearer) return true;
  const exp = decodeJwtPayload(bearer)?.exp;
  if (typeof exp !== "number") return false;
  return exp * 1e3 <= Date.now() + EXPIRY_SKEW_MS;
}
function isAuthTokenExpired(env, bearer) {
  if (env.statusCode !== 401 && env.statusCode !== 403) return false;
  if (env.statusCode === 401) return true;
  if (bearerExpired(bearer)) return true;
  const msg = (env.error?.message ?? "").toLowerCase();
  return msg.includes("expired") || msg.includes("signature");
}
function parseRetryAfterMs(response) {
  const ms = response.headers.get("Retry-After-Ms");
  if (ms) {
    const n = Number(ms);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const ra = response.headers.get("Retry-After");
  if (ra) {
    const secs = Number(ra);
    if (Number.isFinite(secs)) return Math.max(0, secs * 1e3);
    const at = Date.parse(ra);
    if (!Number.isNaN(at)) return Math.max(0, at - Date.now());
  }
  return void 0;
}
var ApiClient = class {
  static {
    brandClass(this, "api/ApiClient");
  }
  config;
  cache;
  cacheTtlMs;
  /** Coalesces concurrent identical cacheable GETs into a single network call. */
  inflight = singleflight();
  /** Collapses concurrent token-expiry recoveries into a single refresh/re-login. */
  authRecovery = singleflight();
  /**
   * The CANONICAL path (+ query) each cached GET was requested with, by cache
   * key. See {@link invalidateMatching}: callers name reads by the path they
   * requested, while the key is the resolved URL.
   */
  canonicalByKey = /* @__PURE__ */ new Map();
  /** Invalidation also fences in-flight reads; older executions cannot refill the cache. */
  cacheGeneration = 0;
  /** The bearer the last request was built with (routes blue/green, see resolveUrl). */
  lastBearer = null;
  /** See {@link onErrorEnvelope}. */
  errorEnvelopeListeners = /* @__PURE__ */ new Set();
  constructor(config) {
    this.config = config;
    this.cache = config.cache ?? (config.cacheTtlMs === 0 ? NO_CACHE : new MemoryRequestCache());
    this.cacheTtlMs = config.cacheTtlMs ?? 3e4;
    if (!isRegionEnabled(this.regionToFullName(config.region))) {
      log2.warn(
        `region ${config.region} is not marked enabled in commons/utils/aws_region_helpers; requests will still attempt.`
      );
    }
  }
  regionToFullName(regionOrAlias) {
    if (regionOrAlias.includes("-")) return regionOrAlias;
    return ALIAS_TO_REGION[regionOrAlias] ?? regionOrAlias;
  }
  resolveBaseUrl() {
    return pickActiveRegionUrl(this.config.baseUrl, this.config.backupBaseUrl);
  }
  /** Verbose-debug flag: explicit config wins, else fe-commons URL/sticky flag. */
  debugOn() {
    return this.config.debug ?? isDebugEnabled2();
  }
  /**
   * Build a {@link ResolvedRegion} pinning a single region (alias or full AWS
   * region), bypassing the token-driven resolution. Used by the per-request
   * `region` override so a write can be fanned out to a specific region's host.
   */
  overrideResolvedRegion(override) {
    if (!override) return void 0;
    const entry = REGION_ALIASES.find((r) => r.alias === override || r.region === override);
    const alias = entry?.alias ?? override;
    const region = entry?.region ?? override;
    return {
      useRegion: region,
      useRegionAlias: alias,
      useBackupRegion: region,
      useBackupRegionAlias: alias
    };
  }
  async buildHeaders(extra, regionOverride) {
    const override = this.overrideResolvedRegion(regionOverride);
    if (this.config.routing) {
      const token2 = this.config.routing.getToken();
      if (token2) {
        const resolved = override ?? resolveRegion(token2, this.config.routing);
        const headers2 = buildAutheHeaders(token2, this.config.routing, resolved, {
          "Content-Type": "application/json",
          ...extra ?? {}
        });
        const live = await Promise.resolve(this.config.getAccessToken()).catch(() => null);
        if (live) {
          headers2.Authorization = `Bearer ${live}`;
          this.lastBearer = live;
        }
        if (this.debugOn()) headers2["X-Debug"] = "1";
        return headers2;
      }
    }
    const token = await Promise.resolve(this.config.getAccessToken());
    const region = override?.useRegion ?? this.config.region;
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Source-Platform": this.config.application,
      "Source-Region": region,
      "Destination-Region": region,
      ...extra ?? {}
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (this.debugOn()) headers["X-Debug"] = "1";
    return headers;
  }
  /** Resolve the full request URL — routing mode applies the verbatim rewrite. */
  resolveUrl(rawPath, query, method = "GET", regionOverride) {
    const path = this.config.rewritePath ? this.config.rewritePath(rawPath, method) : rawPath;
    const qs = buildQueryString(query);
    if (this.config.routing) {
      const snapshot = this.config.routing.getToken();
      const token = snapshot && this.lastBearer ? routingTokenForBearer(snapshot, this.lastBearer, this.config.routing.claimsNamespace) : snapshot;
      if (token) {
        const resolved = this.overrideResolvedRegion(regionOverride) ?? resolveRegion(token, this.config.routing);
        if (this.config.regionApiRouting) {
          const regional = rewriteRegionApiUrl(`${path}${qs}`, token, this.config.routing, resolved, method);
          if (regional) return regional;
        } else {
          return rewriteUrl(`${path}${qs}`, token, this.config.routing, resolved);
        }
      }
    }
    return `${this.resolveBaseUrl()}${path}${qs}`;
  }
  /**
   * Run the {@link ApiClientConfig.onAuthError} hook (de-duped across concurrent
   * expiries) and report whether the caller should retry. A throwing hook is
   * treated as "don't retry" so a recovery failure never masks the original
   * auth error.
   */
  async recoverFromAuthError() {
    const hook = this.config.onAuthError;
    if (!hook) return false;
    return this.authRecovery.run("auth", async () => {
      try {
        return await Promise.resolve(hook()) === true;
      } catch (error) {
        log2.warn("auth recovery hook threw", {
          error: error instanceof Error ? error.message : String(error)
        });
        return false;
      }
    });
  }
  /**
   * Refuse, before any request, a caller whose auth type may not use this app.
   *
   * Throws a PERMISSION error (`PERM001`), not a bare `Error`. A bare `Error`
   * carries no code or status, so `toErrorResponse` could only classify it as
   * `INT001` — and the private share viewer, whose `application` was once
   * missing from the gateway-auth table so this fired for every user, told each
   * of them "Internal server error / contact support if persistent". It was
   * neither internal nor on a server: it never left the browser, retrying could
   * not help, and support was the wrong person to ask. A refusal is a refusal.
   */
  assertCanAccess() {
    if (this.config.authType === void 0 || this.config.authType === null) return;
    if (!canAccessApplication(this.config.authType, this.config.application)) {
      throw new AutheError(ErrorCode.PERM001, {
        message: `Auth type "${this.config.authType}" cannot access application "${this.config.application}"`,
        details: { authType: this.config.authType, application: this.config.application }
      });
    }
  }
  /**
   * Be told about every `ok:false` envelope this client resolves, from any
   * caller. Returns the unsubscribe.
   *
   * For a host that must react to a KIND of refusal wherever it happens —
   * a plan refusal (`PERM003`) means the host's copy of the plan is stale, and
   * the write that met it may be in any package, behind any adapter, with or
   * without a caller that inspects the error. Every one of those writes rides
   * this client, so this is the one place that sees them all.
   *
   * Fired once per network execution (coalesced callers share one), after
   * retries and auth recovery have settled, and never for a cache hit (only
   * successes are cached). A listener that throws is logged and skipped: an
   * observer must not turn into the request's failure.
   */
  onErrorEnvelope(listener) {
    this.errorEnvelopeListeners.add(listener);
    return () => {
      this.errorEnvelopeListeners.delete(listener);
    };
  }
  notifyErrorEnvelope(response, method, path) {
    if (response.ok !== false || this.errorEnvelopeListeners.size === 0) return response;
    for (const listener of [...this.errorEnvelopeListeners]) {
      try {
        listener(response, { method, path });
      } catch (error) {
        log2.warn("error-envelope listener threw", { path, method, error: String(error) });
      }
    }
    return response;
  }
  async request(path, opts = {}) {
    this.assertCanAccess();
    const method = opts.method ?? "GET";
    const url = this.resolveUrl(path, opts.query, method);
    const body = opts.rawBody != null ? opts.rawBody : opts.body == null ? void 0 : JSON.stringify(opts.body);
    const ttl = opts.cacheTtlMs ?? (method === "GET" ? this.cacheTtlMs : 0);
    const key = cacheKey(method, url, body);
    const cacheable = method === "GET" && ttl > 0;
    const coalescable = method === "GET";
    const cacheGeneration = this.cacheGeneration;
    if (cacheable) {
      const hit = this.cache.get(key);
      if (hit) return hit;
    }
    if (coalescable) {
      const sharedOpts = { ...opts, signal: void 0 };
      const run = () => this.execute({ path, query: opts.query, method, body, ttl, key, opts: sharedOpts, cacheGeneration });
      return this.inflight.run(`${cacheGeneration}:${key}`, run);
    }
    return this.execute({ path, query: opts.query, method, body, ttl, key, opts, cacheGeneration });
  }
  /**
   * Resolve the request URL + headers together, waiting for the auth token to be
   * ready. Two cold-load hazards this addresses:
   *  - **routing mode** resolves the URL from the JWT (region/continent host
   *    swap). If the token hasn't been pushed to the router yet, the URL froze to
   *    the fallback baseUrl (the `api.` edge — no localhost CORS → status 0). We
   *    wait (bounded) for `routing.getToken()` before resolving, then build the
   *    correct regional URL.
   *  - **non-routing mode** reads the access token async; `buildHeaders` is
   *    retried so a transient `getAccessToken()` rejection doesn't lose the
   *    request.
   * Both degrade gracefully (proceed with the fallback) once the budget is spent,
   * and honour abort. Called per network attempt so a token landing mid-flight is
   * picked up on the next try.
   */
  async resolveRequest(path, query, method, extra, signal, regionOverride) {
    const budget = this.config.authRetries ?? 5;
    if (this.config.routing) {
      for (let attempt = 0; attempt < budget && !this.config.routing.getToken(); attempt++) {
        if (signal?.aborted) break;
        log2.warn("waiting for routing token before resolving URL", { path, method, attempt });
        await delay(Math.min(150 * 2 ** attempt, 2e3), signal);
      }
    }
    const headers = await retryAsync(() => this.buildHeaders(extra, regionOverride), {
      retries: budget,
      baseMs: 150,
      maxMs: 2e3,
      signal,
      shouldRetry: (_error, attempt) => {
        if (signal?.aborted) return false;
        log2.warn("retrying access-token acquisition", { path, method, attempt });
        return true;
      }
    });
    const url = this.resolveUrl(path, query, method, regionOverride);
    if (this.config.requestSigner) {
      Object.assign(headers, await this.config.requestSigner({ url, method }));
    }
    return { url, headers };
  }
  /** One network execution (shared by coalesced callers), with backoff retries. */
  async execute(ctx) {
    const { path, query, method, body, ttl, key, opts } = ctx;
    const retries = opts.retries ?? this.config.retries ?? 1;
    const timeoutMs = opts.timeoutMs ?? this.config.timeoutMs ?? 3e4;
    const backoff = { baseMs: 250, maxMs: 5e3, ...this.config.retryBackoff, ...opts.retryBackoff };
    let retryAfterMs = 0;
    let bearer = null;
    const runAttempts = () => retryAsync(
      async () => {
        const { url, headers } = await this.resolveRequest(path, query, method, opts.headers, opts.signal, opts.region);
        bearer = sentBearer(headers);
        return this.attempt(url, method, headers, body, opts.signal, timeoutMs, opts.exposeHeaders, opts.credentials, opts.responseType);
      },
      {
        retries,
        ...backoff,
        signal: opts.signal,
        // `attempt` returns terminal envelopes (2xx + non-retryable 4xx/5xx)
        // and throws on retryable failures; stop only when the caller aborts.
        shouldRetry: () => !opts.signal?.aborted,
        onRetry: (error, attempt, delayMs) => {
          retryAfterMs = error instanceof RetryableHttpError ? error.retryAfterMs ?? 0 : 0;
          log2.warn("retrying request", { path, method, attempt, delayMs: Math.max(delayMs, retryAfterMs) });
        },
        sleep: (ms, signal) => delay(Math.max(ms, retryAfterMs), signal)
      }
    );
    try {
      let envelope = await runAttempts();
      if (!envelope.ok && !opts.signal?.aborted && this.config.onAuthError && isAuthTokenExpired(envelope, bearer)) {
        const retry = await this.recoverFromAuthError();
        if (retry && !opts.signal?.aborted) {
          log2.warn("retrying request after auth-token refresh", { path, method });
          envelope = await runAttempts();
        }
      }
      if (method === "GET" && ttl > 0 && envelope.ok && ctx.cacheGeneration === this.cacheGeneration) {
        this.cache.set(key, envelope, ttl);
        this.canonicalByKey.set(key, `${path}${buildQueryString(opts.query)}`);
      }
      return this.notifyErrorEnvelope(envelope, method, path);
    } catch (err) {
      if (err instanceof RetryableHttpError) {
        return this.notifyErrorEnvelope(err.envelope, method, path);
      }
      return this.notifyErrorEnvelope(
        {
          statusCode: 0,
          ok: false,
          error: {
            message: err instanceof Error ? err.message : "Request aborted or network error",
            error_code: "EXT001"
          }
        },
        method,
        path
      );
    }
  }
  /** A single fetch attempt. Throws on retryable failures (5xx / network / timeout). */
  async attempt(url, method, headers, body, signal, timeoutMs, exposeHeaders, credentials, responseType) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const onCallerAbort = () => ctrl.abort(signal?.reason);
    if (signal?.aborted) ctrl.abort(signal.reason);
    else signal?.addEventListener("abort", onCallerAbort, { once: true });
    const start = typeof performance !== "undefined" ? performance.now() : Date.now();
    try {
      const response = await fetch(url, {
        method,
        headers,
        body,
        signal: ctrl.signal,
        ...credentials ? { credentials } : {}
      });
      const durationMs = (typeof performance !== "undefined" ? performance.now() : Date.now()) - start;
      this.config.onResponse?.({ url, status: response.status, durationMs });
      if (!response.ok) {
        const envelope = toErrorEnvelope(await safeJson(response), response);
        if (isRetryableResponse(response.status, errorCodeOf(envelope.error))) {
          throw new RetryableHttpError(envelope, parseRetryAfterMs(response));
        }
        return envelope;
      }
      const exposed = pickHeaders(response, exposeHeaders);
      if (responseType === "text") {
        const text = await response.text();
        return { statusCode: response.status, ok: true, data: text, ...exposed ? { headers: exposed } : {} };
      }
      const json = await safeJson(response);
      if (json && typeof json === "object" && "ok" in json) {
        const env = json;
        if (env.ok === true && env.data === void 0 && "result" in env) {
          env.data = env.result;
        }
        if (this.debugOn()) debugResourceSink.capture(url, json);
        if (exposed && json.ok) json.headers = { ...json.headers, ...exposed };
        return json;
      }
      return { statusCode: response.status, ok: true, data: json ?? void 0, ...exposed ? { headers: exposed } : {} };
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onCallerAbort);
    }
  }
  /** Invalidate a cached GET (call after a mutation that changes it). */
  invalidate(path, query) {
    this.cacheGeneration += 1;
    this.cache.delete(cacheKey("GET", this.resolveUrl(path, query)));
  }
  /**
   * Invalidate every cached GET whose URL contains `fragment`.
   *
   * The exact-URL {@link invalidate} only reaches a read the caller can name in
   * full, query string included. That is rarely the read a write stales: one
   * resource is fetched at several scopes and with several sets of query params
   * by different surfaces, and the writer knows none of them. Without this, a
   * refetch issued straight after a successful write is served the PRE-write
   * body out of the 30s response cache, and the UI looks like the write was
   * dropped until the entry expires or the tab reloads.
   *
   * Matched against the resolved URL AND the canonical path the read was
   * requested with, so pass the same fragment the reads use (`"/addresses/"`,
   * `"/integration-versions"`). Matching the resolved URL alone missed every
   * service behind a rewrite: `/integration-versions` resolves to
   * `…/integrations-mgmt-api/blue/GET/US/v0/versions`, which contains neither
   * the fragment nor its rewrite (the verb and continent land mid-path) — so a
   * build list stayed stale after a deploy and a grant list after a grant.
   *
   * Falls back to clearing the whole cache when the configured store cannot
   * enumerate — over-invalidating costs a few refetches; under-invalidating
   * shows stale data as fact.
   */
  invalidateMatching(fragment) {
    this.cacheGeneration += 1;
    const keys = this.cache.keys?.();
    if (!keys) {
      this.cache.clear();
      this.canonicalByKey.clear();
      return;
    }
    const live = new Set(keys);
    for (const key of this.canonicalByKey.keys()) {
      if (!live.has(key)) this.canonicalByKey.delete(key);
    }
    for (const key of keys) {
      if (!key.startsWith("GET ")) continue;
      const canonical = this.canonicalByKey.get(key);
      if (key.includes(fragment) || canonical !== void 0 && canonical.includes(fragment)) {
        this.cache.delete(key);
        this.canonicalByKey.delete(key);
      }
    }
  }
  get(path, opts) {
    return this.request(path, { ...opts, method: "GET" });
  }
  post(path, body, opts) {
    return this.request(path, { ...opts, method: "POST", body });
  }
  patch(path, body, opts) {
    return this.request(path, { ...opts, method: "PATCH", body });
  }
  put(path, body, opts) {
    return this.request(path, { ...opts, method: "PUT", body });
  }
  delete(path, opts) {
    return this.request(path, { ...opts, method: "DELETE" });
  }
  /** Low-level escape hatch returning the unwrapped `.result` (throws on `ok:false`). */
  static unwrap = unwrapEnvelope;
};
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
function toErrorEnvelope(parsed, response) {
  const fallbackMessage = response.statusText || "Request failed";
  if (parsed && typeof parsed === "object") {
    const body = parsed;
    const nested = body.error;
    if (nested && typeof nested === "object") {
      const err = nested;
      return {
        statusCode: response.status,
        ok: false,
        error: { ...err, message: err.message || fallbackMessage }
      };
    }
    const flat = body;
    const awsMessage = typeof body.Message === "string" ? body.Message : void 0;
    return {
      statusCode: response.status,
      ok: false,
      error: { ...flat, message: flat.message || awsMessage || fallbackMessage }
    };
  }
  return { statusCode: response.status, ok: false, error: { message: fallbackMessage } };
}
function pickHeaders(response, keys) {
  if (!keys || keys.length === 0) return void 0;
  const out = {};
  for (const key of keys) {
    const value = response.headers.get(key);
    if (value != null) out[key.toLowerCase()] = value;
  }
  return Object.keys(out).length > 0 ? out : void 0;
}

// src/api/errors-bridge.ts
var DEFAULT_ERROR_TYPE = "ApiError";
function errorEnvelopeToErrorBody(envelope) {
  const { error } = envelope;
  return {
    message: error.message,
    error_type: DEFAULT_ERROR_TYPE,
    error_code: errorCodeOf(error),
    error_level: error.error_level ?? null,
    details: error.details ?? {},
    correlation_id: error.correlation_id ?? null
  };
}
function errorEnvelopeToErrorResponse(envelope) {
  return {
    statusCode: envelope.statusCode,
    ok: false,
    error: errorEnvelopeToErrorBody(envelope)
  };
}
function thrownToErrorEnvelope(err) {
  const response = toErrorResponse(err);
  const { error } = response;
  return {
    statusCode: response.statusCode,
    ok: false,
    error: {
      message: error.message,
      ...error.error_code ? { error_code: error.error_code } : {},
      ...error.error_level ? { error_level: error.error_level } : {},
      ...error.details ? { details: error.details } : {},
      ...error.correlation_id ? { correlation_id: error.correlation_id } : {}
    }
  };
}

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  static {
    brandClass(this, "api/ApiError");
  }
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function isErrorEnvelope(res) {
  return res.ok === false;
}
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/api/outcome.ts
function readOutcome(found, error) {
  if (error !== void 0 && error !== null) {
    return error.statusCode === 404 ? "missing" : "failed";
  }
  return found ? "ready" : "failed";
}
function listOutcome(args) {
  if (args.count > 0) return "ready";
  if (args.loading) return "loading";
  return args.failed ? "failed" : "empty";
}

// src/api/wire.ts
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function rowsFromWire(data) {
  const rows = Array.isArray(data) ? data : isRecord(data) ? data.items : void 0;
  return Array.isArray(rows) ? rows.filter(isRecord) : [];
}

// src/api/paging.ts
var WALK_PAGE_SIZE = 100;
var WALK_MAX_PAGES = 20;
var IncompleteWalkError = class extends Error {
  constructor(path, pages) {
    super(`${path}: still paging after ${pages} pages`);
    this.path = path;
    this.pages = pages;
    this.name = "IncompleteWalkError";
  }
  path;
  pages;
  static {
    brandClass(this, "api/IncompleteWalkError");
  }
};
async function walkPages(client, path, {
  query,
  signal,
  pageSize = WALK_PAGE_SIZE,
  maxPages = WALK_MAX_PAGES,
  rowsOf = rowsFromWire,
  cacheTtlMs,
  requireComplete = false
} = {}) {
  const rows = [];
  let cursor;
  for (let page = 0; page < maxPages; page += 1) {
    const params = {
      ...query ?? {},
      limit: String(pageSize),
      ...cursor ? { cursor } : {}
    };
    const payload = unwrapData(
      await client.get(path, {
        query: params,
        signal,
        ...cacheTtlMs === void 0 ? {} : { cacheTtlMs }
      })
    );
    rows.push(...rowsOf(payload));
    const next = isRecord(payload) ? payload.next_cursor : void 0;
    cursor = typeof next === "string" && next ? next : void 0;
    if (!cursor) break;
  }
  if (cursor && requireComplete) throw new IncompleteWalkError(path, maxPages);
  return rows;
}

export { ApiClient, ApiError, DEFAULT_REGION_ALIAS, IncompleteWalkError, LocalStorageRequestCache, MemoryRequestCache, NO_CACHE, PRIVATE_BARE_MOUNTS, REGION_ALIASES, STREAMING_SERVICE, WALK_MAX_PAGES, WALK_PAGE_SIZE, applyBlueGreen, appsyncColourUrl, buildAutheHeaders, cacheKey, convertRegionToBackupRegion, debugResourceSink, deploymentColourForService, errorEnvelopeToErrorBody, errorEnvelopeToErrorResponse, isErrorEnvelope, isRecord, listOutcome, localStoragePrefix, pickActiveRegionUrl, readDebugResources, readOutcome, readResponseDebug, regionAlias, resolveRegion, rewriteRegionApiUrl, rewriteRegionGraphqlUrl, rewriteUrl, routingForToken, routingTokenForBearer, rowsFromWire, thrownToErrorEnvelope, unwrapData, unwrapEnvelope, unwrapResponse, walkPages };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map